import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Button,
  DialogActions as _DialogActions,
  DialogContent,
  DialogTitle as _DialogTitle,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { createElement, FC, SyntheticEvent } from 'react';
import { FormattedMessage } from 'react-intl';

import Language from '../../../entity/Language';
import ProductPricing from '../../../entity/ProductPricing';
import { ProjectTypeForListFragment } from '../../../generated/graphql';
import { itemBackground } from '../../constant/color';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import Dialog from '../primitive/DialogWithPadding';
import LfToBr from '../primitive/LfToBr';
import VideoScreen from '../primitive/VideoScreen';

type Props = {
  projectType: ProjectTypeForListFragment;
  open: boolean;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  urltoId: string;
  language?: Language;
  onRequestClose: (e: SyntheticEvent<HTMLElement>) => void;
  className?: string;
};

const ProjectTypeDetailDialogForTrial: FC<Props> = ({
  projectType,
  open,
  urltoId,
  language,
  onRequestClose,
  className,
}) => {
  const redirectUrl = language === 'ja-JP' ? `${urltoId}/account_setting/new` : `${urltoId}/questionnaire/kaizen_ad_us`;

  let preview;
  if (projectType.sampleVideoUrl) {
    preview = <StyledVideo src={projectType.sampleVideoUrl} loop autoPlay />;
  } else if (projectType.thumbnailImageUrl) {
    preview = <StyledImage src={projectType.thumbnailImageUrl} />;
  } else {
    preview = <StyledImage src="/assets/empty_thumbnail.png" />;
  }

  return (
    <Dialog
      open={open}
      onRequestClose={onRequestClose}
      className={className}
      maxWidth="md"
      classes={{ paper: dialogStyle }}
    >
      <DialogTitle disableTypography>{projectType.title}</DialogTitle>

      <CloseButton color="default" onClick={onRequestClose}>
        <CloseIcon />
      </CloseButton>

      <Content>
        <PreviewContainer>{preview}</PreviewContainer>

        {projectType.description ? (
          <Description variant="body2">
            <LfToBr text={projectType.description} />
          </Description>
        ) : null}
      </Content>

      <DialogActions>
        <BusinessAccountButton
          color="primary"
          variant="contained"
          size="large"
          href={redirectUrl}
          target="_blank"
          onClick={onRequestClose}
        >
          <FormattedMessage id="project_type_trial_dialog.business_account_button" />
        </BusinessAccountButton>

        <Message variant="caption">
          <FormattedMessage id="project_type_trial_dialog.business_account_text" />
        </Message>
      </DialogActions>
    </Dialog>
  );
};

const Content = styled(DialogContent)({
  width: 860,
});

const Description = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});

const PreviewContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  marginBottom: SPACING_UNIT * 3,
  backgroundColor: itemBackground,
  border: `1px solid ${itemBackground}`,
});

const StyledVideo = styled(VideoScreen)({
  minWidth: 200,
  maxWidth: 600,
  minHeight: 200,
  maxHeight: '50vh',
  marginBottom: -1, // To hide an odd bottom border
});

const StyledImage = styled('img')({
  minWidth: 200,
  maxWidth: '100%',
  minHeight: 200,
  maxHeight: '50vh',
});

const DialogActions = styled(_DialogActions)(
  gridLayoutContainer({
    columns: ['auto'],
    rows: ['auto', 'auto'],
    areas: [['button'], ['message']],
  }),
  {
    justifyContent: 'center',
    margin: SPACING_UNIT,
  },
);

const BusinessAccountButton = (styled(Button)(gridLayoutItem('button'), {
  marginRight: SPACING_HUGE,
  marginLeft: SPACING_HUGE,
}) as unknown) as typeof Button;

const Message = styled(Typography)(gridLayoutItem('message'), {
  margin: SPACING_UNIT,
  textAlign: 'center',
});

const CloseButton = styled(Button)({
  top: 0,
  right: 0,
  position: 'absolute',
  padding: SPACING_UNIT,
  minWidth: SPACING_UNIT * 3,
  minHeight: SPACING_UNIT * 3,
});

const DialogTitle = styled(_DialogTitle)({
  position: 'relative',
});

const dialogStyle = css({
  maxHeight: SPACING_HUGE * 12,
}).toString();

export default ProjectTypeDetailDialogForTrial;
